import React, { useEffect, useRef, useState } from "react";

import { Alert, Button, Icon, Input, Radio, RadioGroup, StatePanel, Toggle, useToast } from "../../components/ui";
import { waBroadcastApi, apiError } from "../../waBroadcast/waBroadcastApi";
import { WaTemplatesPanel } from "./WaTemplatesPanel";

import "../../waBroadcast/wa-broadcast.scss";

/**
 * Owner Admin → WhatsApp Broadcast (Alex, 2026-10-13):
 *  - one number per person (default) or one shared by the store — any store
 *    with the "WhatsApp Broadcast" row (`wa_shared_number`) may share; the
 *    whole section is hidden without it (MasterAdminSettings);
 *  - whether listing messages carry the item's link;
 *  - the closing lines a message ends with (at least three; one of them, or
 *    none, is picked per group).
 * Everything saves the moment it changes — no Save button.
 */
export function WaNumbersPanel() {
  const toast = useToast();
  const [state, setState] = useState(null);
  const [lines, setLines] = useState([]);
  const [save, setSave] = useState("idle"); // idle | saving | saved
  const [error, setError] = useState("");
  const savedTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    waBroadcastApi
      .numberMode()
      .then((res) => {
        if (!alive) return;
        setState(res);
        setLines(res.closing_lines || []);
      })
      .catch((err) => alive && setError(apiError(err, "Could not load this setting.")));
    return () => {
      alive = false;
      clearTimeout(savedTimer.current);
    };
  }, []);

  const persist = async (patch, onSaved) => {
    const previous = state;
    setState({ ...state, ...patch });
    setSave("saving");
    setError("");
    try {
      const next = await waBroadcastApi.setNumberMode(patch);
      setState(next);
      setLines(next.closing_lines || []);
      setSave("saved");
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSave("idle"), 2500);
      onSaved?.(next);
    } catch (err) {
      setState(previous);
      setLines(previous.closing_lines || []);
      setSave("idle");
      setError(apiError(err, "Couldn't save — try again."));
    }
  };

  if (!state) {
    return error ? <Alert tone="error" title={error} /> : <StatePanel state="loading" loadingShape="row" compact />;
  }

  const min = state.min_closing_lines || 3;
  const filled = lines.map((l) => l.trim()).filter(Boolean);
  const saveLines = (next) => {
    const clean = next.map((l) => l.trim()).filter(Boolean);
    if (clean.length < min) {
      setError(`Keep at least ${min} closing lines.`);
      return;
    }
    if (JSON.stringify(clean) === JSON.stringify(state.closing_lines)) return;
    persist({ closing_lines: clean }, () => toast.success("Closing lines saved", { description: "New messages end with one of these, or with none." }));
  };

  return (
    <div className="wa-numbers-panel">
      <p className="wa-numbers-panel__lead">Please choose how your team should use the broadcasting function.</p>

      <RadioGroup
        legend="How your team uses WhatsApp Broadcast"
        hideLegend
        layout="cards"
        value={state.mode}
        onChange={(mode) =>
          mode !== state.mode &&
          persist({ mode }, () =>
            toast.success(mode === "shared" ? "The store now shares one WhatsApp number" : "Each person now uses their own WhatsApp number", {
              description: mode === "shared"
                ? "Next: link the shared number once on the WhatsApp Broadcast page (in the menu) (if it isn't linked yet). Every owner and store admin then sends from it."
                : "Next: each admin links their own number on the WhatsApp Broadcast page (in the menu).",
            })
          )
        }
      >
        <Radio
          value="per_user"
          label="Each person links their own number"
          description="Every admin links and sends from their own WhatsApp number, with their own groups and lists. Nobody else can see or use it."
        />
        <Radio
          value="shared"
          disabled={!state.shared_allowed}
          label="One shared number for the whole store"
          description={
            state.shared_allowed
              ? "One store number, linked once. Every owner and store admin sends from it and shares its groups and lists."
              : "WhatsApp Broadcast is not included in your store's plan."
          }
        />
      </RadioGroup>
      <p className="wa-numbers-panel__note">Numbers linked under the other option stay linked, just hidden until you switch back.</p>

      <div className="wa-numbers-panel__block">
        <Toggle
          size="md"
          checked={!!state.include_link}
          onChange={(next) =>
            persist({ include_link: next }, () =>
              toast.success(next ? "Listing messages include the link" : "Listing messages are sent without a link")
            )
          }
          label="Include the listing link in messages"
        />
        <p className="wa-numbers-panel__note">
          When on, each listing message ends with a link to the watch. Switch off to send the details and photo only.
        </p>
      </div>

      <div className="wa-numbers-panel__block">
        <h4 className="wa-numbers-panel__title rc-wab-heading">Closing lines</h4>
        <p className="wa-numbers-panel__note">
          Each group gets one of these at the end of the message, or none — so messages aren't all identical. Keep at least {min}.
        </p>
        <ul className="wa-lines">
          {lines.map((line, i) => (
            <li key={i} className="wa-lines__row">
              <Input
                value={line}
                maxLength={120}
                placeholder="e.g. DM for details."
                aria-label={`Closing line ${i + 1}`}
                onChange={(e) => setLines(lines.map((l, j) => (j === i ? e.target.value : l)))}
                onBlur={() => saveLines(lines)}
                onKeyDown={(e) => e.key === "Enter" && saveLines(lines)}
                fullWidth
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove closing line ${i + 1}`}
                disabled={filled.length <= min && line.trim() !== ""}
                onClick={() => {
                  const next = lines.filter((_, j) => j !== i);
                  setLines(next);
                  saveLines(next);
                }}
              >
                <Icon name="Trash2" size="sm" />
              </Button>
            </li>
          ))}
        </ul>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Icon name="Plus" size="sm" />}
          disabled={lines.length >= 20}
          onClick={() => setLines([...lines, ""])}
        >
          Add a closing line
        </Button>
      </div>

      <WaTemplatesPanel />

      <p className="wa-numbers-panel__note" aria-live="polite">
        {save === "saving" ? "Saving…" : save === "saved" ? "All changes saved ✓" : ""}
      </p>
      {error && <Alert tone="error" title={error} />}
    </div>
  );
}

export default WaNumbersPanel;
